import React from 'react';
import { useTranslation } from 'react-i18next';
import Table from '../../components/Table';
import NoContent from '../../components/NoContent';
import TableSkeleton from '../../components/Skeletons/TableSkeleton';
import { hasPermission } from '../../config/rbac';
import { roleLabel } from '../../utils/roleLabel';

const UserListTable = ({
  userLogin,
  usersList,
  searchKey,
  loggedinUserRole,
  refreshUserData,
  handleToggleButtonClick,
  handleResetPasswordButtonClick,
  handleEditUserButtonClick,
  handleDeleteUserButtonClick,
}) => {
  const { t } = useTranslation();
  // Team Management read is granted to claimsManager (per enterprise-standard
  // RBAC), but writes remain admin-only. Without these gates the FE rendered
  // every action button (toggle-active, reset-password, edit, delete) for
  // claimsManager and adjuster — and reset-password + toggleActive were
  // ungated on the backend, so those clicks actually succeeded.
  const canUpdate = hasPermission(loggedinUserRole, 'users', 'update');
  const canDelete = hasPermission(loggedinUserRole, 'users', 'delete');

  return (
    <Table
      thead={
        <tr>
          <th />
          <th>{t('screens.users.colName')}</th>
          <th>{t('screens.users.colEmail')}</th>
          <th>{t('screens.users.colPhone')}</th>
          <th>{t('screens.users.colAddress')}</th>
          {loggedinUserRole === 'admin' && <th>{t('screens.users.colClient')}</th>}
          <th>{t('screens.users.colRole')}</th>
          <th>{t('screens.users.colAllocatedSpace')}</th>
          <th>{t('screens.users.colStatus')}</th>
          <th>{t('screens.users.colActions')}</th>
        </tr>
      }
      tbody={
        usersList && usersList.loading ? (
          <TableSkeleton rows={loggedinUserRole === 'admin' ? 10 : 9} />
        ) : usersList &&
          usersList.userInfo &&
          usersList.userInfo.status === 'success' &&
          usersList.userInfo.data &&
          usersList.userInfo.data.data &&
          usersList.userInfo.data.data.length > 0 ? (
          usersList.userInfo.data.data
            // .filter(user => user.name.toLowerCase().includes(searchKey.toLowerCase()))
            .map(user => (
              <tr key={user._id}>
                <td className='table__profile_container'>
                  <img src='/assets/user.png' alt={user.name} className='table__profile_image' />
                </td>
                <td className='table__row_title'>{user.name}</td>
                <td>{user.email}</td>
                <td>{user.phone}</td>
                <td>{user.address && user.address.length > 20 ? user.address.slice(0, 20) + '...' : user.address}</td>
                {loggedinUserRole === 'admin' && <td>{user?.org?.name ? user?.org?.name : '-'}</td>}
                {/* One resolver, shared with the Add/Edit and filter
                    dropdowns — see utils/roleLabel.js. This cell used to
                    camelCase-split the raw enum, which is neither translated
                    nor vocabulary-aware, so a MOK roster read "Claims
                    Manager" three rows above a dropdown offering "Case
                    Manager". */}
                <td>{roleLabel(t, user.role)}</td>
                <td>{user.isUserUnderStoragePlan ? user.maxStorageAllowed.toFixed(2) + ' GB' : '-'}</td>
                <td>
                  <div className='flex'>
                    {user.active ? (
                      <p className='table__status'>
                        <span>•</span> {t('screens.users.statusActive')}
                      </p>
                    ) : (
                      <p className='table__status_deactive'>
                        <span>•</span> {t('screens.users.statusDeactive')}
                      </p>
                    )}
                    {canUpdate && userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user._id !== user._id && (
                      <label className='switch'>
                        <input id={user._id} checked={user.active} onChange={() => handleToggleButtonClick(user)} type='checkbox' className='checkbox' name='active' />
                        <span className='slider round' />
                      </label>
                    )}
                  </div>
                </td>
                {userLogin.userInfo && userLogin.userInfo.data && userLogin.userInfo.data.user && userLogin.userInfo.data.user._id !== user._id ? (
                  <td className='table__buttons'>
                    {/* Refresh calls /folders/update-folder-id for the TARGET
                        user — a mutation on somebody else's record, not a
                        read. It was the one action in this column with no
                        permission gate, so a claimsManager (users:read only)
                        was offered it on every row but their own. */}
                    {canUpdate && (
                      <button className='table__button' onClick={() => refreshUserData(user)}>
                        <svg className='table__button_icon'>
                          <use xlinkHref={`/assets/sprite.svg#icon-refresh`} />
                        </svg>
                        <p className='table__button_label'>{t('screens.users.actionRefresh')}</p>
                      </button>
                    )}
                    {canUpdate && (
                      <button className='table__button' onClick={() => handleResetPasswordButtonClick(user)}>
                        <svg className='table__button_icon'>
                          <use xlinkHref={`/assets/sprite.svg#icon-lock`} />
                        </svg>
                        <p className='table__button_label'>{t('screens.users.actionResetPassword')}</p>
                      </button>
                    )}
                    {canUpdate && (
                      <button className='table__button' onClick={() => handleEditUserButtonClick(user)}>
                        <svg className='table__button_icon'>
                          <use xlinkHref={`/assets/sprite.svg#icon-edit`} />
                        </svg>
                        <p className='table__button_label'>{t('screens.users.actionEdit')}</p>
                      </button>
                    )}
                    {canDelete && (
                      <button className='table__button_delete' onClick={() => handleDeleteUserButtonClick(user)}>
                        <svg className='table__button_delete_icon'>
                          <use xlinkHref={`/assets/sprite.svg#icon-trash`} />
                        </svg>
                        <p className='table__button_label'>{t('screens.users.actionDelete')}</p>
                      </button>
                    )}
                  </td>
                ) : (
                  <td className='table__buttons'></td>
                )}
              </tr>
            ))
        ) : (
          <tr className='noContent_table_row'>
            <td colSpan={8}>
              <NoContent />
            </td>
          </tr>
        )
      }
    />
  );
};

export default UserListTable;
